import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultSeasonsExportFieldNames, seasonsExportFields } from "~pages/Seasons/SeasonListPage/constants/exportFields.ts";
import extractSeasonsFromScopeByCompetitionId from "~pages/Seasons/SeasonListPage/hooks/extractSeasonsFromScopeByCompetitionId.ts";
import { useDeleteSeason } from "~pages/Seasons/SeasonListPage/hooks/useDeleteSeason.ts";
import { useExportSeasons } from "~pages/Seasons/SeasonListPage/hooks/useExportSeasons.ts";
import { useGetSeasonsTableColumns } from "~pages/Seasons/SeasonListPage/hooks/useGetSeasonsTableColumns.tsx";
import { useGetSeasonsTableFilters } from "~pages/Seasons/SeasonListPage/hooks/useGetSeasonsTableFilters.tsx";
import { useImportSeasons } from "~pages/Seasons/SeasonListPage/hooks/useImportSeasons.ts";
import filterSeasons from "~utils/filterSeasons.ts";

const SeasonListPage = () => {
  const { sport, customerId, organizationId, competitionId } = useParams();
  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.seasons.listPage;
  const paginationConfig = useHandlePagination(50, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const location = useLocation();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  const { mutateAsync: startImporting } = useImportSeasons();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSeasons();
  const dataTableImportConfiguration = uiConfiguration.pages.seasons.listPage.importConfig;

  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/${competitionId}/seasons/add`);
  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteSeason();

  const handleDelete = useCallback(
    async (seasonId: string) => {
      return await deleteFn(seasonId);
    },
    [deleteFn],
  );

  const filtersConfig = useGetSeasonsTableFilters();
  const columns = useGetSeasonsTableColumns();

  const allSeasons = useMemo(
    () => (scopeData ? extractSeasonsFromScopeByCompetitionId(scopeData.scope.leagueTree, scopeData.resources, competitionId) : []),
    [scopeData, competitionId],
  );

  const clientSeasons = useMemo(
    () => filterSeasons(allSeasons, filtersConfig.currentFilter, searchText),
    [allSeasons, filtersConfig.currentFilter, searchText],
  );
  const refetch = async () => {
    await queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user", sport, organizationId] });
  };

  const competitionsData = scopeData?.resources.competitions || {};
  const competitionName = competitionsData[competitionId as string]?.nameLocal;

  return (
    <Page title={competitionName}>
      <DataTable
        handleRefresh={() => {
          refetch();
        }}
        title={formatMessage({ id: "seasonsListPage.title" })}
        columns={columns}
        rowActions={{
          base: {
            delete: {
              onClick: (row) => handleDelete(row.seasonId),
              loading: isDeletePending,
            },
            view: {
              onClick: (row) => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${row.seasonId}/details`),
            },
          },
        }}
        rowKey={"seasonId"}
        rowNameKey={"nameLocal"}
        dataSource={clientSeasons}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasons`}
        isDataLoading={isLoading}
        handleAdd={handleAdd}
        addButtonIntlKey={"season"}
        filtersConfig={{
          ...filtersConfig,
          filtersLocalStorageKey: `${sport}.tables.settings.filters.seasons`,
        }}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.seasons`,
          defaultSorter: {
            columnKey: "year",
            order: "descend",
          },
        }}
        exportConfig={{
          handleExport: startExport,
          loading: isExportPending,
          defaultFields: defaultSeasonsExportFieldNames,
          groupedFields: seasonsExportFields,
          localStorageKey: "export.seasons",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        permissionNames={[PermissionNames.SEASON]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, total: clientSeasons.length }}
      />
    </Page>
  );
};

export default SeasonListPage;
